/**
 * Firebase configuration constants and collection paths
 *
 * Centralized configuration for Firestore collections, storage paths,
 * and other Firebase service configurations.
 */

/**
 * Firebase configuration object
 * Loaded from environment variables
 */
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/**
 * Get Firebase client config from environment variables
 * For use in browsers (web and CMS frontend)
 */
export function getClientConfig(): FirebaseConfig {
  // Cast avoids depending on Vite's ambient `ImportMetaEnv` typing, which
  // isn't present when this file is type-checked from a non-Vite consumer
  // (e.g. the Next.js-based CMS) and would otherwise fail to compile there.
  const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};

  // Next.js (the CMS) doesn't populate import.meta.env the way Vite does —
  // it statically inlines exact `process.env.NEXT_PUBLIC_*` expressions at
  // build time instead, so each fallback below must be written out inline
  // (not aliased to a variable) for that inlining to find it. The
  // `typeof process !== 'undefined'` guard keeps this safe under Vite too,
  // where `process` isn't a real browser global.
  const config: FirebaseConfig = {
    apiKey:
      env.VITE_FIREBASE_API_KEY ||
      (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_FIREBASE_API_KEY : undefined) ||
      '',
    authDomain:
      env.VITE_FIREBASE_AUTH_DOMAIN ||
      (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN : undefined) ||
      '',
    projectId:
      env.VITE_FIREBASE_PROJECT_ID ||
      (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID : undefined) ||
      '',
    storageBucket:
      env.VITE_FIREBASE_STORAGE_BUCKET ||
      (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET : undefined) ||
      '',
    messagingSenderId:
      env.VITE_FIREBASE_MESSAGING_SENDER_ID ||
      (typeof process !== 'undefined'
        ? process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID
        : undefined) ||
      '',
    appId:
      env.VITE_FIREBASE_APP_ID ||
      (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_FIREBASE_APP_ID : undefined) ||
      '',
    measurementId:
      env.VITE_FIREBASE_MEASUREMENT_ID ||
      (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID : undefined),
  };

  // Validate required fields
  const required = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missing = required.filter((key) => !config[key as keyof FirebaseConfig]);

  if (missing.length > 0) {
    console.warn(
      `Missing Firebase config values: ${missing.join(', ')}. ` +
      'Please check your .env file and ensure VITE_FIREBASE_* (or NEXT_PUBLIC_FIREBASE_* for the CMS) variables are set.'
    );
  }

  return config;
}

/**
 * Get Firebase Admin config from environment variables
 * For use in Node.js (Cloud Functions, backend)
 */
export function getAdminConfig(): {
  projectId: string;
  databaseURL?: string;
} {
  const env = typeof process !== 'undefined' ? process.env : undefined;
  // Avoid FIREBASE_* env keys in Cloud Functions dotenv (reserved by Firebase CLI).
  return {
    projectId:
      env?.GCLOUD_PROJECT ||
      env?.GOOGLE_CLOUD_PROJECT ||
      env?.PROJECT_ID ||
      '',
    databaseURL: env?.DATABASE_URL || env?.FIREBASE_DATABASE_URL,
  };
}

/**
 * Firestore Collection Names
 * Use these constants to avoid typos and maintain consistency
 */
export const FIRESTORE_COLLECTIONS = {
  // User management
  USERS: 'users',
  USER_STATS: 'stats',
  USER_ACTIVITY: 'activity',
  USER_PREFERENCES: 'preferences',

  // Projects
  PROJECTS: 'projects',
  PROJECT_VERSIONS: 'versions',
  PROJECT_COMMENTS: 'comments',
  PROJECT_COLLABORATORS: 'collaborators',

  // Templates
  TEMPLATES: 'templates',
  TEMPLATE_CATEGORIES: 'template-categories',

  // Shared
  ACTIVITY_LOG: 'activity-log',
  AUDIT_LOG: 'audit-log',
} as const;

/**
 * Firestore collection paths (for subcollections)
 * These are relative paths used in documents
 */
export const COLLECTION_PATHS = {
  userProjects: (userId: string) => `${userId}/projects`,
  projectVersions: (projectId: string) => `${projectId}/versions`,
  projectComments: (projectId: string) => `${projectId}/comments`,
  projectCollaborators: (projectId: string) => `${projectId}/collaborators`,
} as const;

/**
 * Firebase Storage bucket paths
 * Organize uploads by resource type
 */
export const STORAGE_PATHS = {
  // User avatars
  userAvatars: (userId: string) => `users/${userId}/avatar`,

  // Project data
  projectThumbnails: (userId: string, projectId: string) =>
    `projects/${userId}/${projectId}/thumbnail`,
  projectExports: (userId: string, projectId: string) =>
    `projects/${userId}/${projectId}/exports`,
  projectUploads: (userId: string, projectId: string) =>
    `projects/${userId}/${projectId}/uploads`,
  projectReels: (userId: string, projectId: string) =>
    `projects/${userId}/${projectId}/reels`,
  // Motion timeline media (music, video clips, posters)
  projectVideoAssets: (userId: string, projectId: string) =>
    `projects/${userId}/${projectId}/video`,

  // Standalone PDF Studio documents
  pdfDocuments: (userId: string, docId: string) => `pdf/${userId}/${docId}/source.pdf`,
  pdfDocumentThumb: (userId: string, docId: string) =>
    `pdf/${userId}/${docId}/thumb.jpg`,
  pdfSignatures: (userId: string, signatureId: string) =>
    `pdf/${userId}/signatures/${signatureId}.png`,

  // Template previews
  templatePreviews: (templateId: string) => `templates/${templateId}/preview`,
  templateThumbnails: (templateId: string) => `templates/${templateId}/thumbnails`,

  // Temporary uploads (auto-deleted)
  tempUploads: (userId: string) => `temp/${userId}`,

  // Public assets
  publicAssets: () => 'public/assets',
} as const;

/**
 * Emulator configuration
 * Used for local development
 */
export const EMULATOR_CONFIG = {
  // Emulator ports (must match firebase.json)
  FIRESTORE_PORT: 8080,
  REALTIME_DB_PORT: 9000,
  AUTH_PORT: 9099,
  STORAGE_PORT: 4000,
  PUBSUB_PORT: 8085,

  // Emulator hosts
  LOCALHOST: 'localhost',
  HOST_IP: '127.0.0.1',
} as const;

/**
 * Firebase rules and settings
 */
export const FIRESTORE_RULES = {
  // Document size limits (bytes)
  MAX_DOCUMENT_SIZE: 1_000_000, // 1 MB

  // Batch operation limits
  MAX_BATCH_WRITES: 500,

  // Index requirements
  REQUIRES_COMPOSITE_INDEX: false,
} as const;

/**
 * Storage rules and settings
 */
export const STORAGE_RULES = {
  // File size limits (bytes)
  MAX_FILE_SIZE: 100_000_000, // 100 MB
  MAX_THUMBNAIL_SIZE: 5_000_000, // 5 MB

  // Allowed MIME types
  ALLOWED_IMAGE_TYPES: [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
  ],
  ALLOWED_DOCUMENT_TYPES: [
    'application/pdf',
    'application/json',
    'text/csv',
  ],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/webm', 'video/quicktime'],
  ALLOWED_AUDIO_TYPES: [
    'audio/mpeg',
    'audio/mp4',
    'audio/aac',
    'audio/wav',
    'audio/ogg',
    'audio/webm',
  ],
  MAX_VIDEO_FILE_SIZE: 100_000_000, // 100 MB
  MAX_AUDIO_FILE_SIZE: 20_000_000, // 20 MB
} as const;

/**
 * Authentication configuration
 */
export const AUTH_CONFIG = {
  // Sign-in methods
  SIGN_IN_METHODS: [
    'google.com',
    'github.com',
    'password',
    'anonymous',
  ],

  // Session duration (milliseconds)
  SESSION_DURATION: 1000 * 60 * 60 * 24 * 30, // 30 days

  // Token refresh interval
  TOKEN_REFRESH_INTERVAL: 1000 * 60 * 55, // 55 minutes

  // Persistence settings
  PERSISTENCE_TYPE: 'LOCAL' as const, // 'LOCAL', 'SESSION', or 'NONE'
} as const;

/**
 * Check if running in development environment
 */
export function isDevelopment(): boolean {
  const importMetaEnv = (import.meta as { env?: Record<string, string | undefined> }).env;
  const viteDev = importMetaEnv?.DEV;
  const nodeDev =
    typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
  return Boolean(viteDev || nodeDev);
}

/**
 * Check if Firebase emulators should be used.
 * Opt-in only — set VITE_USE_EMULATORS=true or USE_EMULATORS=true to enable.
 */
export function shouldUseEmulators(): boolean {
  const importMetaEnv = (import.meta as { env?: Record<string, string | undefined> }).env;
  const viteEmulators = importMetaEnv?.VITE_USE_EMULATORS === 'true';
  const nodeEmulators =
    typeof process !== 'undefined' && process.env?.USE_EMULATORS === 'true';

  return Boolean(viteEmulators || nodeEmulators);
}

/**
 * Get the appropriate Firestore host based on environment
 */
export function getFirestoreHost(): string | undefined {
  if (!shouldUseEmulators()) return undefined;
  return `${EMULATOR_CONFIG.LOCALHOST}:${EMULATOR_CONFIG.FIRESTORE_PORT}`;
}

/**
 * Get the appropriate Auth emulator URL based on environment
 */
export function getAuthEmulatorUrl(): string | undefined {
  if (!shouldUseEmulators()) return undefined;
  return `http://${EMULATOR_CONFIG.LOCALHOST}:${EMULATOR_CONFIG.AUTH_PORT}`;
}

/**
 * Get the appropriate Storage bucket path based on environment
 */
export function getStorageBucketUrl(): string | undefined {
  if (!shouldUseEmulators()) return undefined;
  return `${EMULATOR_CONFIG.LOCALHOST}:${EMULATOR_CONFIG.STORAGE_PORT}`;
}
